import { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { mediaVideoStreamingUploadCancelAction } from '../reducers/media-video/action'
import { messageBoxHideAction } from 'src/reducers/message-box/action'

const useLeavePageWarning = () => {
  const dispatch = useDispatch()
  const [showWarning, setShowWarning] = useState(false)
  const [preventDialogs, setPreventDialogs] = useState(false)

  const streamingUploads = useSelector(
    state => state.mediaVideo.streamingUploads,
  )

  // Check if there are active uploads
  const hasActiveUploads = Object.values(streamingUploads).some(
    upload =>
      upload.status === 'uploading' ||
      upload.status === 'processing' ||
      upload.status === 'transcoding',
  )

  // Cancel all active uploads
  const cancelAllUploads = useCallback(() => {
    Object.values(streamingUploads).forEach(upload => {
      if (
        upload.status === 'uploading' ||
        upload.status === 'processing' ||
        upload.status === 'transcoding'
      ) {
        dispatch(mediaVideoStreamingUploadCancelAction(upload.id))
      }
    })
  }, [streamingUploads, dispatch])

  // Handle beforeunload event (page refresh/close)
  const handleBeforeUnload = useCallback(
    event => {
      if (hasActiveUploads && !preventDialogs) {
        const message = 'Changes you made may not be saved.'
        event.preventDefault()
        event.returnValue = message
        return message
      }
    },
    [hasActiveUploads, preventDialogs],
  )

  // Handle popstate event (browser back/forward)
  const handlePopState = useCallback(
    event => {
      if (hasActiveUploads && !preventDialogs) {
        event.preventDefault()
        setShowWarning(true)
        // Push the current state back to prevent navigation
        window.history.pushState(null, '', window.location.href)
      }
    },
    [hasActiveUploads, preventDialogs],
  )

  // Handle warning dialog actions
  const handleLeave = useCallback(() => {
    setShowWarning(false)
    cancelAllUploads()
    // Allow navigation to proceed
    window.history.back()
  }, [cancelAllUploads])

  const handleCancel = useCallback(() => {
    setShowWarning(false)
    dispatch(messageBoxHideAction())
  }, [])

  const handlePreventDialogs = useCallback(() => {
    setPreventDialogs(true)
    // Store in localStorage to persist across sessions
    localStorage.setItem('preventLeavePageDialogs', 'true')
  }, [])

  // Set up event listeners
  useEffect(() => {
    // Check if user previously chose to prevent dialogs
    const storedPreventDialogs = localStorage.getItem('preventLeavePageDialogs')
    if (storedPreventDialogs === 'true') {
      setPreventDialogs(true)
    }

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    // Push initial state to enable popstate detection
    window.history.pushState(null, '', window.location.href)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [handleBeforeUnload, handlePopState])

  return {
    showWarning,
    handleLeave,
    handleCancel,
    handlePreventDialogs,
    hasActiveUploads,
  }
}

export default useLeavePageWarning
